import type { ScrollMomentumState } from './types';
import type PropertyListSection from '~/components/PropertyList/PropertyListSection.vue';
import type { Ref } from 'vue';

import { reactive, ref } from 'vue';

import { DRAG_CONSTANTS } from './constants';

export function useScrollMomentum(
	scrollContainer:
		| Ref<InstanceType<typeof PropertyListSection> | null>
		| undefined,
	headerDragOffsetY: Ref<number>,
	isScreenBelowBreakpoint: Ref<boolean>
) {
	// Momentum state
	const momentumState = reactive<ScrollMomentumState>({
		velocity: 0,
		isActive: false,
		lastTime: 0,
		lastPosition: 0,
		lastRawMovement: 0,
		velocityHistory: []
	});

	let momentumAnimationId: number | null = null;

	function resetScrollState(): void {
		momentumState.lastRawMovement = 0;
		momentumState.velocity = 0;
		momentumState.lastTime = 0;
		momentumState.lastPosition = 0;
		momentumState.velocityHistory.length = 0;
	}

	function handleUpwardScroll(rawMovement: number): void {
		const scrollElement = scrollContainer?.value?.$el;
		if (!scrollElement) return;

		const currentTime = performance.now();

		// Initialize scroll mode if needed
		if (momentumState.lastRawMovement === 0) {
			momentumState.lastRawMovement = rawMovement;
			momentumState.lastTime = currentTime;
			momentumState.lastPosition = scrollElement.scrollTop;
			momentumState.velocityHistory.length = 0;
			return;
		}

		// Calculate movement delta and apply scroll
		const movementDelta = rawMovement - momentumState.lastRawMovement;
		const timeDelta = currentTime - momentumState.lastTime;
		const scrollAmount =
			movementDelta * DRAG_CONSTANTS.SCROLL_RESPONSIVENESS_FACTOR;

		// Calculate velocity for history
		if (timeDelta > 0) {
			const currentVelocity = (scrollAmount / timeDelta) * 16; // Normalize for 60fps
			addVelocityToHistory(currentVelocity, currentTime);
		}

		// Apply scroll
		const newScrollTop = scrollElement.scrollTop + scrollAmount;
		scrollElement.scrollTop = Math.max(0, newScrollTop);

		// Update header offset for upward drag
		updateHeaderOffsetForUpwardScroll(scrollAmount);

		// Update tracking variables
		momentumState.lastRawMovement = rawMovement;
		momentumState.lastTime = currentTime;
		momentumState.lastPosition = scrollElement.scrollTop;
	}

	function handleDownwardScroll(rawMovement: number): void {
		const scrollElement = scrollContainer?.value?.$el;
		if (!scrollElement) return;

		const currentTime = performance.now();

		// Initialize scroll mode if needed
		if (momentumState.lastRawMovement === 0) {
			momentumState.lastRawMovement = rawMovement;
			momentumState.lastTime = currentTime;
			momentumState.lastPosition = scrollElement.scrollTop;
			momentumState.velocityHistory.length = 0;
			return;
		}

		// Calculate movement delta and apply scroll
		const movementDelta = rawMovement - momentumState.lastRawMovement;
		const timeDelta = currentTime - momentumState.lastTime;
		const scrollAmount =
			movementDelta * DRAG_CONSTANTS.SCROLL_RESPONSIVENESS_FACTOR;

		// Calculate velocity for history
		if (timeDelta > 0) {
			const currentVelocity = (-scrollAmount / timeDelta) * 16; // Negative for downward
			addVelocityToHistory(currentVelocity, currentTime);
		}

		// Apply scroll (negative for downward)
		const newScrollTop = Math.max(0, scrollElement.scrollTop - scrollAmount);
		scrollElement.scrollTop = newScrollTop;

		// Update header offset for downward drag
		updateHeaderOffsetForDownwardScroll(scrollAmount);

		// Update tracking variables
		momentumState.lastRawMovement = rawMovement;
		momentumState.lastTime = currentTime;
		momentumState.lastPosition = scrollElement.scrollTop;

		// Reset if scroll reaches the top
		if (scrollElement.scrollTop <= 0) {
			resetScrollState();
		}
	}

	function addVelocityToHistory(velocity: number, time: number): void {
		momentumState.velocityHistory.push({ velocity, time });

		if (
			momentumState.velocityHistory.length >
			DRAG_CONSTANTS.MAX_VELOCITY_HISTORY_LENGTH
		) {
			momentumState.velocityHistory.shift();
		}
	}

	function updateHeaderOffsetForUpwardScroll(scrollAmount: number): void {
		if (isScreenBelowBreakpoint.value && scrollAmount > 0) {
			// Make header hiding more aggressive for upward scrolls
			const scrollVelocityNormalized = Math.min(
				Math.abs(scrollAmount) / DRAG_CONSTANTS.HEADER_VELOCITY_DIVISOR,
				1.5 // Increased from 1 to allow faster header hiding
			);
			const targetOffset = Math.min(
				DRAG_CONSTANTS.MAX_HEADER_OFFSET,
				headerDragOffsetY.value +
					scrollVelocityNormalized * DRAG_CONSTANTS.HEADER_VELOCITY_MULTIPLIER
			);
			headerDragOffsetY.value = Math.max(
				0,
				Math.min(DRAG_CONSTANTS.MAX_HEADER_OFFSET, targetOffset)
			);
		}
	}

	function updateHeaderOffsetForDownwardScroll(scrollAmount: number): void {
		if (isScreenBelowBreakpoint.value && scrollAmount > 0) {
			// Make header showing more responsive for downward scrolls
			const scrollVelocityNormalized = Math.min(
				Math.abs(scrollAmount) / DRAG_CONSTANTS.HEADER_VELOCITY_DIVISOR,
				1.5 // Increased from 1 to allow faster header showing
			);
			const targetOffset = Math.max(
				0,
				headerDragOffsetY.value -
					scrollVelocityNormalized * DRAG_CONSTANTS.HEADER_VELOCITY_MULTIPLIER
			);
			headerDragOffsetY.value = Math.max(
				0,
				Math.min(DRAG_CONSTANTS.MAX_HEADER_OFFSET, targetOffset)
			);
		}
	}

	function startMomentumScroll(): void {
		const scrollElement = scrollContainer?.value?.$el;
		if (!scrollElement) return;

		// Calculate average velocity
		let finalVelocity = 0;

		if (momentumState.velocityHistory.length >= 2) {
			// Use weighted average for recent velocities
			let totalWeight = 0;
			let weightedSum = 0;

			momentumState.velocityHistory.forEach((entry, index) => {
				const weight = index + 1;
				weightedSum += entry.velocity * weight;
				totalWeight += weight;
			});

			finalVelocity = weightedSum / totalWeight;
		} else if (
			momentumState.velocityHistory.length === 1 &&
			momentumState.velocityHistory[0]
		) {
			finalVelocity = momentumState.velocityHistory[0].velocity || 0;
		}

		// Only start momentum if velocity is significant
		if (Math.abs(finalVelocity) < DRAG_CONSTANTS.MOMENTUM_THRESHOLD) {
			return;
		}

		// Apply momentum multiplier
		momentumState.velocity = finalVelocity * DRAG_CONSTANTS.MOMENTUM_MULTIPLIER;
		momentumState.isActive = true;

		animateMomentum();
	}

	function animateMomentum(): void {
		const scrollElement = scrollContainer?.value?.$el;
		if (!scrollElement) {
			stopMomentumScroll();
			return;
		}

		if (!momentumState.isActive || Math.abs(momentumState.velocity) < 0.3) {
			stopMomentumScroll();
			return;
		}

		// Apply scroll with current velocity
		const currentScrollTop = scrollElement.scrollTop;
		const newScrollTop = currentScrollTop + momentumState.velocity;
		const maxScroll = scrollElement.scrollHeight - scrollElement.clientHeight;

		// Check bounds
		if (newScrollTop <= 0) {
			scrollElement.scrollTop = 0;
			stopMomentumScroll();
			return;
		} else if (newScrollTop >= maxScroll) {
			scrollElement.scrollTop = maxScroll;
			stopMomentumScroll();
			return;
		} else {
			scrollElement.scrollTop = newScrollTop;
		}

		// Apply friction
		momentumState.velocity *= DRAG_CONSTANTS.FRICTION_FACTOR;

		// Continue animation
		momentumAnimationId = requestAnimationFrame(animateMomentum);
	}

	function stopMomentumScroll(): void {
		momentumState.isActive = false;
		if (momentumAnimationId) {
			cancelAnimationFrame(momentumAnimationId);
			momentumAnimationId = null;
		}
		resetScrollState();
	}

	function shouldHandleScrollInsteadOfDrag(
		isDraggingUp: boolean,
		actualMovement: number,
		menuLevel: number
	): boolean {
		const scrollElement = scrollContainer?.value?.$el;
		if (!scrollElement) return false;

		if (isDraggingUp) {
			// Make scroll activation more sensitive - activate scroll when closer to top
			return actualMovement <= 5 && menuLevel === 0; // Increased from 2 to 5 for better sensitivity
		} else {
			// For downward drags, be more responsive to scroll position
			return scrollElement.scrollTop > 0;
		}
	}

	return {
		// State
		momentumState: reactive(momentumState),

		// Actions
		handleUpwardScroll,
		handleDownwardScroll,
		startMomentumScroll,
		stopMomentumScroll,
		resetScrollState,
		shouldHandleScrollInsteadOfDrag
	};
}
